import React, { useEffect, useState } from "react";
import { BoxProps } from "@mui/material/Box";
import { Box, Container } from "@mui/material";
import Menu from "@mui/material/Menu";
import { styled, SxProps, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { EmtAvatar } from "./EmtAvatar";
import { EmtDrawer } from "./EmtDrawer";
import { EmtIcon } from "./EmtIcon";
import { EmtTypography } from "./EmtTypography";
import { EmtNavLink } from "./EmtNavLink";
import { EmtSeparator } from "./EmtSeparator";
import { EmtMenuBackground } from "./EmtMenuBackground";
import { EmtTheme } from "../types";
import { useScrollTransform } from "../hooks";

export type EmtNavProps = {
  /**
   * Id of the div
   * @default main-navigation
   */
  id?: string;
  /**
   * @default undefined
   */
  children?: React.ReactNode;
  /**
   * @default undefined
   */
  mobileChildren?: React.ReactNode;
  /**
   * @default undefined
   */
  profilMenuChildren?: React.ReactNode;
  /**
   * @default undefined
   */
  drawerChildren?: React.ReactNode;
  /**
   * Url of user avatar
   * @default undefined
   */
  avatarUrl?: string;
  avatarAlt?: string;
  /**
   * Title for "My profil"
   * @default My Profil
   */
  profilTitle?: string;
  /**
   * Title for drawer
   * @default Menu
   */
  drawerTitle?: string;
  /**
   * @default {}
   */
  sx?: SxProps;
  nonce?: string;
  isQueryOwner?: boolean;
  isDrawerOpen?: boolean;
  toggleDrawer?: () => void;
  closeDrawer?: () => void;
  isProfileMenuOpen?: boolean;
  closeProfileMenu?: () => void;
  openProfileMenu?: () => void;
  shouldTransform?: boolean;
};

const NavBox = styled(Box)(() => ({
  position: "relative",
  maxWidth: "100%",
  background: "white",
  height: "80px",
  padding: "0 24px",
  boxShadow:
    "0px 4px 8px rgba(0, 0, 0, 0.08), 0px 4px 32px rgba(0, 0, 0, 0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  zIndex: 10,
}));

const NavBoxMobile = styled(Box)(() => ({
  minHeight: "70px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
}));

const ProfilMenu = styled(Menu)(() => ({
  "& .MuiPaper-root": {
    padding: "24px",
    borderRadius: "16px",
    zIndex: 120,
    "& ul": {
      background: "white",
      display: "flex",
      flexDirection: "column",
      rowGap: "20px",
    },
  },
}));

type ProfilBoxProps = BoxProps & {
  userNavOpen: boolean;
  theme?: EmtTheme;
};

const ProfilBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "userNavOpen",
})(({ theme, userNavOpen }: ProfilBoxProps) => ({
  color: userNavOpen
    ? theme?.palette.primary.main
    : theme?.palette.common.black,
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  "&:hover": { color: theme?.palette.primary.main },
}));

export function EmtNav(props: EmtNavProps) {
  const {
    id = "main-navigation",
    children,
    mobileChildren,
    avatarUrl,
    avatarAlt,
    profilTitle = "My profil",
    drawerTitle = "Menu",
    profilMenuChildren,
    drawerChildren,
    isQueryOwner = false,
    sx = {},
    isDrawerOpen = false,
    toggleDrawer = () => {},
    closeDrawer = () => {},
    isProfileMenuOpen = false,
    closeProfileMenu = () => {},
    openProfileMenu = () => {},
    shouldTransform = true,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery("(max-width:1080px)");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const userNavOpen = Boolean(anchorEl);
  const handleUserNavClick = (event: React.MouseEvent<HTMLDivElement>) => {
    isTablet
      ? setAnchorEl(document.getElementById("profil-avatar"))
      : isMobile
      ? setAnchorEl(document.getElementById("profil-avatar"))
      : setAnchorEl(document.getElementById("profil-button"));
    openProfileMenu();
  };
  const handleUserNavClose = () => {
    setAnchorEl(null);
    closeProfileMenu();
  };

  useEffect(() => {
    if (!isProfileMenuOpen) {
      setAnchorEl(null);
    }
  }, [isProfileMenuOpen]);

  const { transformStyles } = useScrollTransform({
    translateY: {
      hidden: "180%",
      visible: "0",
    },
  });

  return (
    <>
      {isMobile ? (
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "fixed",
              zIndex: isMobile ? 999 : 999,
              width: "100%",
              background: "white",
              bottom: 0,
              boxShadow:
                "0px 12px 16px rgba(0, 0, 0, 0.08), 0px 4px 56px rgba(0, 0, 0, 0.08)",
              ...(shouldTransform ? transformStyles : {}),
            }}
            className="EmtReactNativeWebViewPaddingBottom"
          >
            <NavBoxMobile id={id} sx={sx}>
              <>{mobileChildren}</>

              <EmtNavLink
                onClick={toggleDrawer}
                className={isDrawerOpen ? "active" : ""}
              >
                <EmtIcon className="emt-menu-1" />
                <EmtTypography
                  variant="body2"
                  sx={{ "& *": { color: "#77757F!important" } }}
                >
                  {drawerTitle}
                </EmtTypography>
              </EmtNavLink>
            </NavBoxMobile>
          </Box>
        </Box>
      ) : (
        <NavBox id={id} sx={sx}>
          <Container
            sx={{
              display: "flex",
              justifyContent: "space-between",
              height: "100%",
            }}
            maxWidth="lg"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: isTablet ? "20px" : "30px",
              }}
            >
              <>{children}</>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  ...(isQueryOwner && {
                    marginRight: "5px",
                    marginBottom: "-2.5px",
                  }),
                }}
                onClick={handleUserNavClick}
              >
                <EmtAvatar
                  id="profil-avatar"
                  variant="rounded"
                  src={avatarUrl}
                  sx={{ border: "2px solid white" }}
                />
                {isQueryOwner && (
                  <EmtAvatar
                    id="profil-avatar-owner"
                    variant="rounded"
                    src={avatarAlt || ""}
                    sx={{
                      position: "absolute",
                      top: "0",
                      left: "0",
                      transform: "translate(5px, -5px)",
                      zIndex: -1,
                    }}
                  />
                )}
              </Box>

              {!isTablet && (
                <ProfilBox
                  id="profil-button"
                  aria-controls={userNavOpen ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={userNavOpen ? "true" : undefined}
                  onClick={handleUserNavClick}
                  userNavOpen={userNavOpen}
                >
                  <EmtTypography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ userSelect: "none" }}
                  >
                    {profilTitle}
                  </EmtTypography>

                  <EmtIcon className="emt-down-1" sx={{ fontWeight: "bold" }} />
                </ProfilBox>
              )}

              <ProfilMenu
                id="profil-menu"
                anchorEl={anchorEl}
                open={isProfileMenuOpen}
                onClose={handleUserNavClose}
                transformOrigin={{
                  horizontal: isTablet ? 175 : 150,
                  vertical: "top",
                }}
                anchorOrigin={{
                  horizontal: "center",
                  vertical: isTablet ? 60 : 50,
                }}
                transitionDuration={50}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                sx={{
                  zIndex: 9999,
                  ...(isQueryOwner && {
                    "& .MuiPaper-root": { padding: "16px" },
                  }),
                }}
              >
                <>{profilMenuChildren}</>
              </ProfilMenu>

              <EmtIcon
                className="emt-menu-1"
                sx={{ fontSize: "18px", marginLeft: "30px", cursor: "pointer" }}
                onClick={toggleDrawer}
              />
            </Box>
          </Container>
        </NavBox>
      )}

      <EmtDrawer
        variant="temporary"
        onTop={true}
        anchor={isMobile ? "bottom" : "right"}
        sx={{ borderRadius: 0 }}
        open={isDrawerOpen}
        onClose={toggleDrawer}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "30px",
            paddingTop: "30px",
            paddingBottom: "100px",
            minWidth: isMobile ? "100%" : "300px",
          }}
          className="EmtReactNativeWebViewMarginTop"
        >
          <EmtTypography
            variant="h4"
            fontWeight={800}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "24px",
            }}
          >
            {drawerTitle}
            <EmtIcon
              className="emt-x-1"
              onClick={closeDrawer}
              sx={{ fontSize: "18px", cursor: "pointer" }}
            />
          </EmtTypography>

          <EmtSeparator />

          <>{drawerChildren}</>
        </Box>
      </EmtDrawer>

      {userNavOpen && <EmtMenuBackground />}
    </>
  );
}
